import { inject, Injectable, Injector } from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { DynamicPageComponent, GenericDynamicComponent, LazyLoadingSettings } from 'src/app/core/classes/DynamicComponent';
import { safeParse } from 'valibot';
import { createBuilderComponentFactory, createInjector, generateUniqueComponentId } from '../helpers/page-component.helper';

@Injectable({
  providedIn: 'root',
})
export class BuilderPageComponentService {
  logger = inject(GenericLogger);
  #injector = inject(Injector);

  async createPageComponent(componentMeta: any): Promise<DynamicPageComponent | null> {
    const builderComponentFactory = createBuilderComponentFactory(this.#injector, componentMeta.component.id);
    const component = await builderComponentFactory.getComponent(componentMeta.component.id, componentMeta.component);

    if (component === undefined) {
      this.logger.error('BuilderPageComponentService: Error creating the page component', componentMeta);
      return null;
    }

    const builderData = this.#getBuilderDataForInputs(component, componentMeta.component.options, componentMeta.component.id);

    // If the validation fails, the component is not returned to the page. Only valid components are returned.
    if (!this.#validatePageComponent(component, builderData, componentMeta.component.id)) {
      return null;
    }

    const injector = createInjector(this.#injector, componentMeta.component.options);
    const lazyLoadingSettings = this.#getLazyLoadingSettings(componentMeta.component.options);
    const componentId = generateUniqueComponentId(componentMeta.component.id);

    return { componentId, component, injector, inputs: builderData, lazyLoadingSettings };
  }

  /**
   * Validates the data model of a Builder Page Component against the schema defined in the component that loads the data.
   * A validation is performed only if the component has the inputsSchemaValidation property defined.
   *
   * @param component - The component that loads the builder data.
   * @param data - The data of the page component.
   * @param id - The name of the page component.
   *
   * @returns true if the validation is successful, false otherwise.
   */
  #validatePageComponent(component: GenericDynamicComponent, data: unknown, id: string): boolean {
    try {
      if (!this.#hasSchemaValidation(component)) {
        this.logger.debug({
          msg: `The component that loads the ${id} data model from builder data does not have the a schema validation defined. Skipping validation.`,
          component,
          data,
        });

        return true;
      }

      if (!data) {
        this.logger.warn({
          msg: `No data found for component that loads the ${id} data model. If the page component is a reference to a data model, check from Builder that the Page Creator that the key is set to 'EntryId'.`,
        });

        return false;
      }

      const validationResult = safeParse(component.inputsSchemaValidation, data); // safeParse is used to avoid throwing an exception
      if (!validationResult.success) {
        this.logger.warn({
          msg: `The component that loads the ${id} data model from builder data has validation issues:`,
          issues: validationResult.issues,
        });

        return false;
      }

      this.logger.debug({
        msg: `The component that loads the ${id} data model from builder data has been validated with result:`,
        validationResult: validationResult.output,
      });

      return true;
    } catch (error) {
      this.logger.error({
        msg: `Error validating the component that loads the ${id} data model from builder:`,
        error,
      });

      return false;
    }
  }

  /**
   * Get the builder data which is used as inputs for the Page Component.
   * The data is only returned if the Page Component has a schema validation defined.
   */
  #getBuilderDataForInputs(component: GenericDynamicComponent, data: any, id: string): any {
    if (!this.#hasSchemaValidation(component)) {
      return null;
    }

    if (!data) {
      this.logger.warn({
        msg: `No data found for component that loads the data model ${id}.`,
      });

      return null;
    }

    // The Page Component is using in the Page Creator by reference.
    // For convention, the Builder Page Component where the type chosen is Reference to a data model, the key should be EntryId.
    if (data.EntryId?.value?.data) {
      return data.EntryId.value.data;
    }

    // The Page Component is using in the Page Creator by value.
    return data;
  }

  #hasSchemaValidation(component: GenericDynamicComponent): boolean {
    return Boolean(component.inputsSchemaValidation);
  }

  #getLazyLoadingSettings(data: any): LazyLoadingSettings | undefined {
    if (!data?.activateLazyLoading) {
      return undefined;
    }

    return {
      activate: data.activateLazyLoading,
      lazyTimeout: data.lazyTimeout,
      scrollOffset: data.scrollOffset,
    };
  }
}
